'use client'

import type { ComponentProps } from 'react'

import { cn } from '../../utils/cn'
import { Text, type TextProps } from './Text'
import { TruncatedTooltip } from './TruncatedTooltip'

interface TruncatedTextProps extends Omit<TextProps, 'children' | 'ref'> {
  /** Text to render on a single line; revealed in a tooltip only when clipped. */
  children: string
  /** Side the reveal tooltip opens on. Defaults to `top`. */
  tooltipSide?: ComponentProps<typeof TruncatedTooltip>['side']
}

/**
 * A single-line `Text` that truncates with an ellipsis and, only when the value
 * is actually clipped, exposes the full string in a tooltip on hover/focus.
 * Relies on the app-level `TooltipProvider` already mounted at the root.
 */
export function TruncatedText({
  children,
  className,
  tooltipSide = 'top',
  ...props
}: TruncatedTextProps) {
  return (
    <TruncatedTooltip text={children} side={tooltipSide}>
      {ref => (
        <Text ref={ref} className={cn('truncate', className)} {...props}>
          {children}
        </Text>
      )}
    </TruncatedTooltip>
  )
}
